/**
 * PredictiveSearch Web Component
 * Provides real-time search suggestions with keyboard navigation and accessibility
 * @extends HTMLElement
 */
class PredictiveSearch extends HTMLElement {
  // Constants
  static DEBOUNCE_DELAY = 300;
  static MIN_SEARCH_LENGTH = 3;
  static CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  static MAX_CACHE_SIZE = 50; // Maximum number of cached searches
  static MAX_RETRY_ATTEMPTS = 2;
  static RETRY_DELAY = 1000;

  constructor() {
    super();
    this.cachedResults = new Map(); // Use Map for better cache management
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances =
      document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener('input', debounce((event) => {
        this.onChange(event);
      }, PredictiveSearch.DEBOUNCE_DELAY).bind(this));

      if (this.input.value != '') {
        this.onFocus();
      }
    }

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    // Focus trap for accessibility
    this.addEventListener('keydown', this.handleFocusTrap.bind(this));
  }

  handleFocusTrap(event) {
    if (!this.getAttribute('open')) return;

    if (event.key === 'Tab') {
      const focusableElements = this.querySelectorAll(
        'input:not([disabled]), button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    this.toggleResetButton();

    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    // Minimum character requirement for predictive search
    if (this.searchTerm.length < PredictiveSearch.MIN_SEARCH_LENGTH) {
      this.closeResults(false);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) event.preventDefault();
  }

  onFormReset(event) {
    event.preventDefault();

    this.input.value = '';
    this.input.focus();
    this.toggleResetButton();
    this.searchTerm = '';
    this.abortController.abort();
    this.abortController = new AbortController();
    this.closeResults(true);
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);

    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
      case 'Escape':
        this.close();
        this.input.blur();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    const currentButtonText = searchForTextElement?.textContent;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.textContent = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  /**
   * Get cached results if available and not expired
   * @param {string} queryKey - Cache key for the search query
   * @returns {string|null} Cached HTML markup or null
   */
  getCachedResults(queryKey) {
    const cached = this.cachedResults.get(queryKey);
    if (cached && Date.now() - cached.timestamp < PredictiveSearch.CACHE_TTL) {
      return cached.markup;
    }
    // Remove expired cache entry
    if (cached) {
      this.cachedResults.delete(queryKey);
    }
    return null;
  }

  /**
   * Manages cache size by removing oldest entries when limit is reached
   * @param {Map} cache - The cache Map to manage
   */
  manageCacheSize(cache) {
    // Remove oldest entries if cache size exceeds limit
    if (cache.size >= PredictiveSearch.MAX_CACHE_SIZE) {
      // Get the oldest entry (first one in the Map)
      const firstKey = cache.keys().next().value;
      cache.delete(firstKey);
    }

    // Also clean up expired entries
    const now = Date.now();
    for (const [key, value] of cache.entries()) {
      if (now - value.timestamp > PredictiveSearch.CACHE_TTL) {
        cache.delete(key);
      }
    }
  }

  /**
   * Fetches search results from API with retry mechanism
   * @param {string} searchTerm - The search query
   * @param {number} retryCount - Current retry attempt
   */
  async getSearchResults(searchTerm, retryCount = 0) {
    const queryKey = searchTerm.replace(/\s+/g, "-").toLowerCase();
    this.setLiveRegionLoadingState();

    // Check cache first
    const cachedMarkup = this.getCachedResults(queryKey);
    if (cachedMarkup) {
      this.renderSearchResults(cachedMarkup);
      return;
    }

    try {
      const response = await fetch(
        `${routes.predictive_search_url}?q=${encodeURIComponent(
          searchTerm
        )}&section_id=predictive-search`,
        { signal: this.abortController.signal }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const text = await response.text();
      const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;

      // Save to cache with timestamp, keeping all instances synced
      const cacheEntry = {
        markup: resultsMarkup,
        timestamp: Date.now()
      };
      this.allPredictiveSearchInstances.forEach(
        (predictiveSearchInstance) => {
          // Manage cache size before adding new entry
          this.manageCacheSize(predictiveSearchInstance.cachedResults);
          predictiveSearchInstance.cachedResults.set(queryKey, cacheEntry);
        }
      );
      this.renderSearchResults(resultsMarkup);
    } catch (error) {
      if (error.name === 'AbortError') {
        // Request was aborted, ignore
        return;
      }

      // Retry logic for network errors
      if (retryCount < PredictiveSearch.MAX_RETRY_ATTEMPTS && !error.message.includes('404')) {
        setTimeout(() => {
          this.getSearchResults(searchTerm, retryCount + 1);
        }, PredictiveSearch.RETRY_DELAY);
        return;
      }

      // Show error state
      this.handleSearchError(error);
    }
  }

  handleSearchError(error) {
    this.removeAttribute('loading');
    const errorMessage = this.getAttribute('data-error-text') || 'Search failed. Please try again.';

    // Create error markup
    const errorMarkup = `
      <div class="predictive-search__error" role="alert">
        <p>${errorMessage}</p>
      </div>
    `;

    this.predictiveSearchResults.innerHTML = errorMarkup;
    this.setLiveRegionText(errorMessage);
    this.open();
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countElement = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countElement) {
      this.setLiveRegionText(countElement.textContent);
    } else {
      // Handle no results case
      const resultsCount = this.querySelectorAll('.predictive-search__list-item').length;
      const message = resultsCount > 0
        ? `${resultsCount} results found`
        : 'No results found';
      this.setLiveRegionText(message);
    }
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');

    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden')
    } else if (this.input.value.length === 0  && !resetIsHidden) {
      this.resetButton.classList.add('hidden')
    }
  }

  /**
   * Cleanup when component is removed from DOM
   */
  disconnectedCallback() {
    // Clear cache to free memory
    if (this.cachedResults) {
      this.cachedResults.clear();
    }

    // Abort any pending requests
    if (this.currentAbortController) {
      this.currentAbortController.abort();
    }

    // Remove event listeners
    if (this.input) {
      this.input.removeEventListener('input', this.debouncedOnChange);
      this.input.form.removeEventListener('submit', this.onFormSubmit);
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
